import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Sparkles, Upload, Trash2 } from 'lucide-react';
import { Category, CategoryFormData } from '@/types/taxonomy';
import { categoryApi } from '@/services/categoryApi';
import { aiApi } from '@/services/aiApi';
import { uploadImageToStorageV2 } from '@/utils/storage';
import toast from 'react-hot-toast';

interface CategoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingCategory?: Category | null;
  parentId?: string | null;
  masterCategories: Category[];
}

export const CategoryDrawer: React.FC<CategoryDrawerProps> = ({
  isOpen,
  onClose,
  onSaved,
  editingCategory,
  parentId,
  masterCategories,
}) => {
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    slug: '',
    description: '',
    order: 0,
    status: 'active',
  });

  const isEditing = !!editingCategory;

  useEffect(() => {
    if (isOpen) {
      if (editingCategory) {
        setFormData({
          name: editingCategory.name,
          slug: editingCategory.slug,
          description: editingCategory.description || '',
          image: editingCategory.image || '',
          order: editingCategory.order,
          status: editingCategory.status,
          parentId: editingCategory.parentId,
        });
      } else {
        setFormData({
          name: '',
          slug: '',
          description: '',
          order: 0,
          status: 'active',
          parentId: parentId || undefined,
        });
      }
    }
  }, [isOpen, editingCategory, parentId]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const sanitizeCategoryDescription = (value: string) => {
    const plain = value
      .replace(/<[^>]*>/g, ' ')
      .replace(/[#*_`>-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const sentenceMatches = plain.match(/[^.!?]+[.!?]?/g) || [];
    let limited = sentenceMatches
      .map((sentence) => sentence.trim())
      .filter(Boolean)
      .slice(0, 3)
      .join(' ')
      .trim();

    if (limited.length > 220) {
      limited = `${limited.slice(0, 217).trim()}...`;
    }

    return limited;
  };

  const handleGenerateDescription = async () => {
    if (!formData.name.trim()) {
      toast.error('Önce kategori adını girin');
      return;
    }

    try {
      setAiLoading(true);
      const result = await aiApi.generateSeo({
        title: formData.name,
        description: formData.description,
        type: 'description',
        entityType: 'category',
      });

      setFormData((prev) => ({
        ...prev,
        description: sanitizeCategoryDescription(result),
      }));
      toast.success('AI ile açıklama oluşturuldu');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'AI açıklama oluşturulamadı');
    } finally {
      setAiLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setImageUploading(true);
      const url = await uploadImageToStorageV2(file, 'categories', 'product');
      setFormData((prev) => ({ ...prev, image: url }));
      toast.success('Görsel yüklendi');
    } catch (err: any) {
      toast.error(err.message || 'Görsel yüklenemedi');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Kategori adı gerekli');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        slug: formData.slug,
        parentId: formData.parentId,
        description: formData.description,
        image: formData.image,
        order: formData.order,
        status: formData.status,
      };

      if (isEditing && editingCategory) {
        await categoryApi.updateCategory(editingCategory.id, payload);
        toast.success('Kategori güncellendi');
      } else {
        await categoryApi.createCategory(payload);
        toast.success('Kategori oluşturuldu');
      }

      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'İşlem başarısız');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 transition-opacity animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* Drawer Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40 flex-shrink-0">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100">
              {isEditing ? '✏️ Kategori Düzenle' : '➕ Yeni Kategori'}
            </h2>
            {isEditing && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-medium truncate max-w-[280px]">
                {editingCategory?.name}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center min-w-[38px] min-h-[38px] hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded-xl transition text-slate-500"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Drawer Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Kategori Adı */}
          <div>
            <label className="input-label">
              Kategori Adı <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                const name = e.target.value;
                setFormData({ ...formData, name, slug: generateSlug(name) });
              }}
              className="input-field"
              placeholder="Örn: Sevgililer Günü"
              autoFocus
            />
          </div>

          {/* Slug */}
          <div>
            <label className="input-label">
              Slug (URL)
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="input-field font-mono text-xs"
              placeholder="sevgililer-gunu"
            />
            <p className="text-[11px] text-slate-400 mt-1">Otomatik oluşturulur</p>
          </div>

          {/* Master Kategori Seçimi */}
          <div>
            <label className="input-label">
              Üst Kategori
            </label>
            <select
              value={formData.parentId || ''}
              onChange={(e) =>
                setFormData({ ...formData, parentId: e.target.value || undefined })
              }
              className="input-field"
            >
              <option value="">— Master Kategori (Üst yok) —</option>
              {masterCategories
                .filter((c) => !editingCategory || c.id !== editingCategory.id)
                .map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    📦 {cat.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Açıklama + AI */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="input-label mb-0">
                Açıklama
              </label>
              <button
                type="button"
                onClick={handleGenerateDescription}
                disabled={aiLoading}
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-50 dark:bg-purple-950/50 px-2.5 py-1 text-xs font-semibold text-purple-600 dark:text-purple-300 border border-purple-200/50 dark:border-purple-800/50 transition hover:bg-purple-100 disabled:opacity-50"
              >
                <Sparkles size={12} className={aiLoading ? 'animate-spin' : ''} />
                {aiLoading ? 'Oluşturuluyor...' : 'AI ile Oluştur'}
              </button>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-field min-h-[90px] py-2"
              placeholder="Kategori hakkında kısa açıklama..."
              rows={3}
            />
          </div>

          {/* Görsel Upload */}
          <div>
            <label className="input-label">
              Kategori Görseli
            </label>
            {formData.image ? (
              <div className="relative group rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
                <img
                  src={formData.image}
                  alt="Kategori görseli"
                  className="w-full h-36 object-cover"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, image: '' })}
                  className="absolute top-2 right-2 p-2 bg-rose-600 text-white rounded-xl shadow-md transition-transform hover:scale-105"
                  title="Görseli Sil"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageUploading}
                className="w-full flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl hover:border-brand-500 transition text-slate-500 dark:text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 bg-slate-50/50 dark:bg-slate-800/30"
              >
                <Upload size={24} />
                <span className="text-xs font-semibold">
                  {imageUploading ? 'Görsel Yükleniyor...' : 'Görsel Yükle (JPG/PNG/HEIC)'}
                </span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Sıra ve Durum */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="input-label">
                Sıra
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) =>
                  setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                }
                className="input-field"
                placeholder="0"
              />
            </div>

            <div>
              <label className="input-label">
                Durum
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as 'active' | 'inactive',
                  })
                }
                className="input-field"
              >
                <option value="active">✅ Aktif</option>
                <option value="inactive">❌ İnaktif</option>
              </select>
            </div>
          </div>
        </div>

        {/* Drawer Footer */}
        <div className="px-5 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="btn btn-secondary flex-1"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !formData.name.trim()}
            className="btn btn-primary flex-1"
          >
            <Save size={16} />
            {loading ? 'Kaydediliyor...' : isEditing ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </div>
    </>
  );
};
